import type { PortalParams } from '../api/siteData';

export const PORTAL_VIEW_SECTIONS = ['core', 'trend', 'session', 'user', 'payUser', 'product'] as const;
export type PortalViewSectionKey = typeof PORTAL_VIEW_SECTIONS[number] | 'vooglamSession';
export const PORTAL_VIEW_LABELS: Record<PortalViewSectionKey, string> = {
  core: '核心指标', trend: '核心指标趋势', session: '会话', user: '用户',
  payUser: '支付用户数', product: '商品', vooglamSession: 'Vooglam 会话多维',
};
export interface PortalViewSection {
  params: Omit<PortalParams, 'site'>;
  quickKey: string;
  dateRule?: { kind: 'rolling'; startOffset: number; endOffset: number } | { kind: 'lastWeek' } | { kind: 'thisWeek' | 'thisMonth' };
}
export interface PortalViewSnapshot {
  sections: Record<typeof PORTAL_VIEW_SECTIONS[number], PortalViewSection> & { vooglamSession?: PortalViewSection };
  trendValueMetricKeys: string[];
  trendRatioMetricKeys: string[];
}
export interface ChannelPortalView { id: string; name: string; snapshot: PortalViewSnapshot; updatedAt: number }
const DAY = 86400000;
const QUICK_KEYS = ['today', 'yesterday', 'recent7', 'recent14', 'recent30', 'thisWeek', 'thisMonth', 'lastWeek'];
const dateOrdinal = (date: string) => Date.parse(date + 'T00:00:00Z') / DAY;
const dateString = (ordinal: number) => new Date(ordinal * DAY).toISOString().slice(0, 10);
function todayOrdinal(now: Date) {
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / DAY;
}
function validDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const ordinal = dateOrdinal(value);
  return Number.isFinite(ordinal) && dateString(ordinal) === value;
}

// 保留各区块当前日期口径，例如核心指标的最近7天不包含今天。
export function capturePortalViewSection(params: PortalParams, quickKey = '', now = new Date()): PortalViewSection {
  const { site: _site, ...filters } = params;
  const section: PortalViewSection = { params: filters, quickKey };
  if (QUICK_KEYS.includes(quickKey) && validDate(params.start_date) && validDate(params.end_date)) {
    section.dateRule = quickKey === 'lastWeek' ? { kind: 'lastWeek' } : quickKey === 'thisWeek' || quickKey === 'thisMonth' ? { kind: quickKey } : {
      kind: 'rolling', startOffset: dateOrdinal(params.start_date) - todayOrdinal(now), endOffset: dateOrdinal(params.end_date) - todayOrdinal(now),
    };
  }
  return section;
}

export function resolvePortalView(snapshot: PortalViewSnapshot, now = new Date()): PortalViewSnapshot {
  const result = structuredClone(snapshot);
  const today = todayOrdinal(now);
  Object.values(result.sections).forEach(section => {
    if (section.dateRule?.kind === 'rolling') {
      section.params.start_date = dateString(today + section.dateRule.startOffset);
      section.params.end_date = dateString(today + section.dateRule.endOffset);
    } else if (section.dateRule?.kind === 'thisWeek' || section.dateRule?.kind === 'thisMonth') {
      const start = section.dateRule.kind === 'thisWeek' ? today - ((now.getDay() + 6) % 7) : Date.UTC(now.getFullYear(), now.getMonth(), 1) / DAY;
      section.params.start_date = dateString(start);
      section.params.end_date = dateString(today);
    } else if (section.dateRule?.kind === 'lastWeek') {
      const monday = today - ((now.getDay() + 6) % 7);
      section.params.start_date = dateString(monday - 7);
      section.params.end_date = dateString(monday - 1);
    }
  });
  return result;
}

export function portalViewFingerprint(snapshot: PortalViewSnapshot): string {
  const normalized = Object.entries(snapshot.sections).sort(([a], [b]) => a.localeCompare(b)).map(([key, section]) => {
    const params = { ...section.params };
    if (section.dateRule) { params.start_date = ''; params.end_date = ''; }
    return [key, Object.entries(params).sort(([a], [b]) => a.localeCompare(b)), section.dateRule ?? null];
  });
  return JSON.stringify([normalized, snapshot.trendValueMetricKeys, snapshot.trendRatioMetricKeys]);
}

export function portalViewSummary(section: PortalViewSection): string {
  const labels: Record<string, string> = { today: '今天', yesterday: '昨天', recent7: '最近7天', recent14: '最近14天', recent30: '最近30天', thisWeek: '本周', thisMonth: '本月', lastWeek: '上个自然周' };
  const { params } = section;
  const dates = section.dateRule ? labels[section.quickKey] : params.start_date === params.end_date ? params.start_date : params.start_date + ' 至 ' + params.end_date;
  const periods: Record<string, string> = { day: '日', week: '周', month: '月', year: '年' };
  return [dates, '按' + (periods[params.period ?? 'day'] ?? '日'), params.channels || '全部渠道'].join(' · ');
}

function isObject(value: unknown): value is Record<string, unknown> { return !!value && typeof value === 'object' && !Array.isArray(value); }
const stringArray = (value: unknown): value is string[] => Array.isArray(value) && value.every(item => typeof item === 'string');
export function isPortalViewSnapshot(value: unknown): value is PortalViewSnapshot {
  if (!isObject(value) || !isObject(value.sections) || !stringArray(value.trendValueMetricKeys) || !stringArray(value.trendRatioMetricKeys)) return false;
  const sections = value.sections;
  if (!PORTAL_VIEW_SECTIONS.every(key => key in sections)) return false;
  return Object.entries(sections).every(([key, section]) => {
    if (!(key in PORTAL_VIEW_LABELS) || !isObject(section) || !isObject(section.params) || typeof section.quickKey !== 'string') return false;
    const p = section.params;
    if (!validDate(p.start_date) || !validDate(p.end_date) || p.start_date > p.end_date || !Object.values(p).every(v => v === undefined || typeof v === 'string')) return false;
    if (!['day', 'week', 'month', 'year'].includes(String(p.period ?? 'day'))) return false;
    if (key === 'payUser' && p.period !== 'week' && p.period !== 'month') return false;
    const rule = section.dateRule;
    if (rule === undefined) return true;
    if (!isObject(rule) || !QUICK_KEYS.includes(section.quickKey)) return false;
    if (rule.kind === 'lastWeek') return section.quickKey === 'lastWeek';
    if (rule.kind === 'thisWeek' || rule.kind === 'thisMonth') return section.quickKey === rule.kind;
    return rule.kind === 'rolling' && Number.isInteger(rule.startOffset) && Number.isInteger(rule.endOffset)
      && Number(rule.startOffset) <= Number(rule.endOffset) && Math.abs(Number(rule.startOffset)) < 36600 && Math.abs(Number(rule.endOffset)) < 36600;
  });
}

export function readPortalViews(raw: string | null): ChannelPortalView[] {
  if (raw === null) return [];
  const data: unknown = JSON.parse(raw);
  if (!isObject(data) || data.version !== 1 || !Array.isArray(data.views) || data.views.length > 20) throw new Error('快捷视图格式无法读取，请保留本地数据并联系管理员');
  const ids = new Set<string>();
  const names = new Set<string>();
  for (const view of data.views) {
    if (!isObject(view) || typeof view.id !== 'string' || !view.id || typeof view.name !== 'string' || !view.name.trim() || view.name.length > 30 || typeof view.updatedAt !== 'number' || !isPortalViewSnapshot(view.snapshot) || ids.has(view.id) || names.has(view.name)) throw new Error('快捷视图数据不完整，请保留本地数据并联系管理员');
    ids.add(view.id); names.add(view.name);
  }
  return data.views as ChannelPortalView[];
}

export function portalViewStorageKey(userID: string, brandID: string, siteID: string): string {
  return 'channel-portal:views:v1:' + [userID, brandID, siteID].map(encodeURIComponent).join(':');
}
